import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  button_base_styles,
  button_size_styles,
} from 'libs/buttons/native/src/components/Button/styles';
import { ButtonSizes } from 'libs/buttons/react/src/lib/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Pressable, StyleProp, TextStyle, ViewStyle } from 'react-native';
import styled from 'styled-components/native';

interface ThrottledButtonProps {
  onChange: (pressed: boolean, timeSincePressed: number) => void;
  size?: ButtonSizes;
  throttle?: number;
  stickyRate?: number;

  icon?: IconProp;
  iconColor?: string;
  iconSize?: number;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;

  disabled?: boolean;

  children?: React.ReactNode;
}

export const ThrottledButton = (props: ThrottledButtonProps) => {
  const { size = 'medium', icon, iconColor, iconSize } = props;
  const [isPressed, setIsPressed] = useState<boolean>(false);
  const pressedTimestamp = useRef<number>(Date.now());

  /**
   * Wrapper of props.onChange that handles throttling
   */
  const throttleTimer = useRef<any>(null);
  const handleOnChange = useCallback(
    (pressed: boolean) => {
      if (props.throttle) {
        // Check if the timeout since last call has passed. If it hasn't, then we don't do anything
        if (throttleTimer.current === null) {
          props.onChange(pressed, Date.now() - pressedTimestamp.current);

          throttleTimer.current = setTimeout(() => {
            throttleTimer.current = null;
          }, props.throttle);
        }
      } else {
        props.onChange(pressed, Date.now() - pressedTimestamp.current);
      }
    },
    [props]
  );

  /**
   * Handle sending data when throttle is held still at the sticky rate
   */
  useEffect(() => {
    if (props.stickyRate && isPressed) {
      const id = setInterval(() => handleOnChange(isPressed), props.stickyRate);

      return () => clearInterval(id);
    }
  }, [handleOnChange, isPressed, props.stickyRate]);

  return (
    <Pressable
      style={[
        button_base_styles as ViewStyle,
        button_size_styles[size],
        { opacity: isPressed ? 0.4 : 1 },
        props.style,
      ]}
      onPressIn={(e) => {
        pressedTimestamp.current = e.timeStamp;
        setIsPressed(true);
        handleOnChange(true);
      }}
      onPressOut={() => {
        setIsPressed(false);
        props.onChange(false, Date.now() - pressedTimestamp.current); // Bypass the throttle checking in handle on change
      }}
      disabled={props.disabled}
    >
      {icon ? (
        <IconContainer>
          <FontAwesomeIcon icon={icon} color={iconColor} size={iconSize} />
          {props.children ? <IconSpacer /> : null}
        </IconContainer>
      ) : null}

      {props.children}
    </Pressable>
  );
};

const IconContainer = styled.View`
  flex-direction: row;
`;

const IconSpacer = styled.View`
  width: 8px;
`;
